import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, MapPinOff, Search, X, Loader2, Smartphone, Monitor, Tablet, Info } from 'lucide-react';
import { searchLocations, LocationSuggestion } from '../../services/campaignService';
import { Switch, SetG } from './SetupSteps';
import type { FormState } from './ManagerForm';
import type { ProfileLocation, TargetingProfile } from '../../services/trafficManagerService';

const VAZIO: TargetingProfile = { locations: [], excluded_locations: [], age_min: null, age_max: null, genders: ['all'], devices: ['mobile', 'desktop', 'tablet'] };
export const perfilDoForm = (f: FormState): TargetingProfile => ({ ...VAZIO, ...(f.guardrails.targeting_profile || {}) });

const IDADES_MIN = [18, 25, 35, 45, 55, 65];
const IDADES_MAX = [24, 34, 44, 54, 64, 65];

const inputCls = 'w-full px-3 py-2.5 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

/** Busca de localização com chips (países, estados, cidades). */
const LocationPicker: React.FC<{ value: ProfileLocation[]; onChange: (v: ProfileLocation[]) => void; placeholder: string; tone: 'include' | 'exclude'; label: string }> = ({ value, onChange, placeholder, tone, label }) => {
  const [q, setQ] = useState('');
  const [res, setRes] = useState<LocationSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const n = ++seq.current;
    setLoading(true);
    const h = setTimeout(async () => {
      const r = await searchLocations(q.trim());
      if (n === seq.current) { setRes(r.slice(0, 8)); setLoading(false); }
    }, 300);
    return () => clearTimeout(h);
  }, [q]);
  const add = (s: LocationSuggestion) => {
    if (!value.some((v) => v.id === s.id)) onChange([...value, { name: s.name, canonicalName: s.canonicalName, id: s.id, targetType: s.targetType, countryCode: s.countryCode }]);
    setQ(''); setRes([]);
  };
  const chip = tone === 'include' ? 'bg-primary-50 text-primary-800 border-primary-200 dark:bg-primary-900/30 dark:text-primary-200 dark:border-primary-800' : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-200 dark:border-red-800';
  return (
    <div>
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-2 mb-2">
          {value.map((v) => (
            <li key={v.id || v.name} className={`inline-flex items-center gap-1 pl-2.5 pr-1 py-1 text-sm rounded-full border ${chip}`}>
              <span title={v.canonicalName}>{v.name}</span>
              <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="p-0.5 rounded-full hover:bg-black/5" aria-label={`${label}: ${v.name}`}><X className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input className={`${inputCls} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} aria-label={label} />
        {loading && <Loader2 className="w-4 h-4 text-gray-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
        {res.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg" role="listbox">
            {res.map((s) => (
              <li key={s.id}>
                <button type="button" onClick={() => add(s)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="min-w-0"><span className="block text-gray-900 dark:text-white truncate">{s.name}</span><span className="block text-xs text-gray-500 truncate">{s.canonicalName} · {s.targetType}</span></span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/**
 * Público e localização do produto. Opcional; quando preenchido vira LIMITE
 * para o robô (ele nunca segmenta fora disto) e ele corrige as campanhas que
 * estiverem fora.
 */
export const StepAudience: React.FC<{ f: FormState; setG: SetG }> = ({ f, setG }) => {
  const { t } = useTranslation();
  const p = perfilDoForm(f);
  const up = (patch: Partial<TargetingProfile>) => setG('targeting_profile', { ...p, ...patch });
  const gen = p.genders[0] || 'all';
  const DEV: [TargetingProfile['devices'][number], string, React.ElementType][] = [
    ['mobile', t('trafficManager.audience.mobile', 'Celular'), Smartphone],
    ['desktop', t('trafficManager.audience.desktop', 'Computador'), Monitor],
    ['tablet', t('trafficManager.audience.tablet', 'Tablet'), Tablet]
  ];
  const seg = (on: boolean) => `px-3 py-2 text-sm rounded-lg border transition-colors ${on ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200 font-medium' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300'}`;
  return (
    <div className="space-y-7">
      <div className="flex gap-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-4 text-sm text-gray-600 dark:text-gray-300">
        <Info className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
        <p>{t('trafficManager.audience.intro', 'Opcional, e vale ouro para produto de nicho. O que você definir aqui vira limite: o robô nunca anuncia fora disso, corrige campanhas que estiverem fora e só corta uma região ou faixa de dentro com dado que prove. Nuances (ex.: "foco em clínicas") vão nas diretrizes.')}</p>
      </div>

      <section>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-1"><MapPin className="w-4 h-4 text-primary-600" />{t('trafficManager.audience.whereQ', 'Onde você vende?')}</h3>
        <p className="text-xs text-gray-500 mb-3">{t('trafficManager.audience.whereHelp', 'Países, estados ou cidades. Vazio: o robô só restringe dentro da área que cada campanha já atende.')}</p>
        <LocationPicker value={p.locations} onChange={(v) => up({ locations: v })} tone="include" label={t('trafficManager.audience.whereQ', 'Onde você vende?') as string} placeholder={t('trafficManager.audience.searchPh', 'Buscar cidade, estado ou país') as string} />
      </section>

      <section>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-1"><MapPinOff className="w-4 h-4 text-red-500" />{t('trafficManager.audience.excludeQ', 'Onde nunca anunciar?')}</h3>
        <p className="text-xs text-gray-500 mb-3">{t('trafficManager.audience.excludeHelp', 'Regiões que você não atende dentro da área acima.')}</p>
        <LocationPicker value={p.excluded_locations} onChange={(v) => up({ excluded_locations: v })} tone="exclude" label={t('trafficManager.audience.excludeQ', 'Onde nunca anunciar?') as string} placeholder={t('trafficManager.audience.searchPh', 'Buscar cidade, estado ou país') as string} />
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        <section>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('trafficManager.audience.ageQ', 'Idade do público')}</h3>
          <div className="flex items-center gap-2">
            <select className={inputCls} value={p.age_min ?? ''} aria-label={t('trafficManager.audience.ageFrom', 'De') as string}
              onChange={(e) => up({ age_min: e.target.value ? Number(e.target.value) : null, ...(e.target.value && p.age_max != null && Number(e.target.value) > p.age_max ? { age_max: null } : {}) })}>
              <option value="">{t('trafficManager.audience.ageAny', 'Qualquer')}</option>
              {IDADES_MIN.map((a) => <option key={a} value={a}>{a === 65 ? '65+' : a}</option>)}
            </select>
            <span className="text-sm text-gray-500 shrink-0">{t('trafficManager.audience.ageTo', 'até')}</span>
            <select className={inputCls} value={p.age_max ?? ''} aria-label={t('trafficManager.audience.ageTo', 'até') as string}
              onChange={(e) => up({ age_max: e.target.value ? Number(e.target.value) : null })}>
              <option value="">{t('trafficManager.audience.ageAny', 'Qualquer')}</option>
              {IDADES_MAX.filter((a) => p.age_min == null || a >= p.age_min).map((a) => <option key={a} value={a}>{a === 65 ? '65+' : a}</option>)}
            </select>
          </div>
          <p className="mt-1.5 text-[11px] text-gray-500">{t('trafficManager.audience.ageHelp', 'Nas faixas do Google (18-24, 25-34...). Deixe "Qualquer" se não tiver certeza: o robô descobre pelos números.')}</p>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('trafficManager.audience.genderQ', 'Gênero')}</h3>
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {(['all', 'male', 'female'] as const).map((g) => (
              <button key={g} type="button" role="radio" aria-checked={gen === g} onClick={() => up({ genders: [g] })} className={seg(gen === g)}>
                {g === 'all' ? t('trafficManager.audience.genderAll', 'Todos') : g === 'male' ? t('trafficManager.audience.genderMale', 'Masculino') : t('trafficManager.audience.genderFemale', 'Feminino')}
              </button>
            ))}
          </div>
        </section>
      </div>

      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('trafficManager.audience.devicesQ', 'Dispositivos')}</h3>
        <div className="flex flex-wrap gap-2">
          {DEV.map(([d, label, Icon]) => {
            const on = p.devices.includes(d);
            return (
              <button key={d} type="button" aria-pressed={on} onClick={() => { const nx = on ? p.devices.filter((x) => x !== d) : [...p.devices, d]; if (nx.length) up({ devices: nx }); }} className={`inline-flex items-center gap-1.5 ${seg(on)}`}>
                <Icon className="w-4 h-4" />{label}
              </button>
            );
          })}
        </div>
      </section>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-4">
        <Switch checked={f.guardrails.allow_targeting} onChange={(v) => setG('allow_targeting', v)}
          label={t('trafficManager.form.g.targeting', 'Pode ajustar localização, idade, gênero e dispositivos das campanhas')}
          help={t('trafficManager.form.g.targetingHelp', 'Sempre dentro do perfil acima, com dado que prove, uma mudança por campanha e depois maturar.')} />
      </div>
    </div>
  );
};

/** Uma linha de resumo do perfil ("SP, RJ · 25-54 · celular"), ou null se vazio. */
export const resumoPerfil = (f: FormState, t: (k: string, d: string) => string): string | null => {
  const p = perfilDoForm(f);
  const partes: string[] = [];
  if (p.locations.length) partes.push(p.locations.map((l) => l.name).slice(0, 3).join(', ') + (p.locations.length > 3 ? ` +${p.locations.length - 3}` : ''));
  if (p.age_min != null || p.age_max != null) partes.push(`${p.age_min ?? 18}-${(p.age_max ?? 65) === 65 ? '65+' : p.age_max}`);
  if (p.genders[0] === 'male') partes.push(t('trafficManager.audience.genderMale', 'Masculino'));
  if (p.genders[0] === 'female') partes.push(t('trafficManager.audience.genderFemale', 'Feminino'));
  if (p.devices.length < 3) partes.push(p.devices.map((d) => t(`trafficManager.audience.${d}`, d)).join('/'));
  return partes.length ? partes.join(' · ') : null;
};
